import React, { useState, useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import { AlertCircle, Loader, Phone, Info } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import API_CONFIG from "../lib/ApiConfig";
import giftTime from "../assets/images/gift-time.svg";
import BuyerInfoModal from "./BuyerInfoModal";
import analyticsService from "../lib/AnalyticsService";
import { useInteractionTracking } from "../hooks/useAnalytics";

const BuyerOfferScreen = ({
  buyerData,
  onAcceptOffer,
  onReject,
  renewal = false,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showBuyerModal, setShowBuyerModal] = useState(false);
  const [error, setError] = useState(null);
  const { getIdToken } = useAuth();
  const { trackButtonClick } = useInteractionTracking();

  useEffect(() => {
    analyticsService.trackPageView("buyer_offer", { buyer: buyerData?.name });
  }, []);

  if (!buyerData) return null;
  // Check if the buyer is SimplyIOA
  const isSimplyIOA = buyerData.name === "SimplyIOA";
  const isAADirect = buyerData.name?.toLowerCase() === "aadirect";
  const isRein = buyerData.name?.toLowerCase() === "rein";
  const isNovo = buyerData.name?.toLowerCase() === "novo";

  const handleOfferResponse = async (accepted) => {
    if (isSimplyIOA) {
      // For SimplyIOA, just continue to consent screen without API call
      onAcceptOffer(buyerData);
      return;
    }

    // For other buyers, make API call to accept offer and process reward
    setIsProcessing(true);
    setError(null);

    try {
      // Get authentication token
      const token = await getIdToken();
      if (!token) throw new Error("Authentication required");

      // Make API call to accept the offer
      let acceptOfferUrl = `${API_CONFIG.BASE_URL}/api/users/insurance-profile/offers/${buyerData.id}/accept`;
      if (renewal) {
        // Retrieve the selected policy from sessionStorage
        const selectedPolicyStr = sessionStorage.getItem(
          "selectedRenewalPolicy"
        );
        const selectedPolicy = selectedPolicyStr
          ? JSON.parse(selectedPolicyStr)
          : null;

        acceptOfferUrl = `${API_CONFIG.BASE_URL}/api/users/insurance-profile/renewal/offers/${buyerData.id}/accept`;
        // Add the policy ID to the request parameters or body
        if (selectedPolicy && selectedPolicy.id) {
          acceptOfferUrl += `?policy=${selectedPolicy.id}`;
        }
      }
      const response = await fetch(acceptOfferUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": token,
        },
        body: JSON.stringify({
          dataBuyerId: buyerData.dataBuyerId,
          amount: buyerData.offer.amount
        }),
      });

      if (!response.ok) {
        throw new Error("Something went wrong. Please try again.");
      }

      // Parse the response to get reward data
      const primaryReward = await response.json();

      const rewardData = {
        id: primaryReward.id,
        dataBuyerId: primaryReward.buyer?.id,
        name: primaryReward.buyer?.name,
        logo: primaryReward.buyer?.logo,
        listLogo: primaryReward.buyer?.listLogo,
        hasExistingReward: true, // Flag to indicate this is an existing reward
        isAADirect: isAADirect,
        isRein: isRein,
        offer: {
          amount: primaryReward.amount,
          currency: "USD",
        },
        description: primaryReward.buyer?.description,
        shortDescription: primaryReward.buyer?.shortDescription,
        shopNowLink: primaryReward.buyer?.shopNowLink,
        allRewards: [primaryReward],
        timestamp: new Date().toISOString(),
      };
      // Continue to next screen with buyer data
      onAcceptOffer(rewardData);
    } catch (err) {
      console.error("Error accepting offer:", err);
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6 flex flex-col h-[calc(100vh-110px)]">
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>
      )}
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-xl font-bold uppercase">
          You’ve Got an Offer from
        </h1>
        <div className="w-20 h-20 bg-violet-100 rounded-full flex items-center justify-center mx-auto !my-2 p-3">
          {/* <Award className="w-8 h-8 text-green-500" /> */}
          <img
            className="w-full h-full object-contain"
            src={buyerData.logo}
            alt="buyerLogo"
          />
        </div>
        <h1 className="text-xl uppercase font-bold text-darkPurple !mt-0">
          {buyerData.name}
        </h1>
        <a
          onClick={() => {
            trackButtonClick("buyer-detail-modal", {
              step_name: "buyer_offer",
            });
            setShowBuyerModal(true);
          }}
          className="underline text-[#1B1B1E]"
        >
          Learn more
        </a>
        <BuyerInfoModal
          open={showBuyerModal}
          onOpenChange={setShowBuyerModal}
          buyer={buyerData}
        />
        <p className="text-lg !mt-6">
          They are excited to work with you and would like to access your
          verified Ownli Insurance Profile and offer you a competitive rate.
        </p>
      </div>
      {/* Co-Branding Banner */}
      {/* <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-3">
          Ownli Logo
          <img
            src="https://cdn.prod.website-files.com/669b34072bcb988e7f8e59fb/66a178ec42ce4055d185f92c_ownli-logo-1.svg"
            alt="Ownli"
            className="h-6 w-auto"
          />
          {buyerData.logo && (
            <img
              src={buyerData.logo}
              alt={buyerData.name}
              className="h-6 w-auto"
            />
          )}
        </div>
        <BadgeCheck className="w-5 h-5 text-blue-500" />
      </div> */}
      {!(isAADirect || isRein || isSimplyIOA || isNovo) && (
        <p className="text-center text-lg !mt-4">
          {buyerData.shortDescription ||
            "They’ve helped thousands of people save on insurance across 48 states."}
        </p>
      )}
      <Card className="shadow-none bg-[#CEC5FF66] border-none !mt-6 rounded-[20px] !mb-4">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <img src={giftTime} alt="giftTime" />
            <p className="text-base text-darkPurple">
              Your reward is pending until your profile is approved
            </p>
          </div>
        </CardContent>
      </Card>
      <div className="w-[174px] w-fit mx-auto flex items-center justify-center p-5 bg-lightGrey rounded-[20px] !my-4">
        <h2 className="text-xl font-bold">
          ${buyerData.offer.amount.toFixed(2)}
        </h2>
      </div>

      {/* NEW SECTION - AADirect specific message */}
      {(isAADirect || isRein) && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-6 !mt-4 !mb-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Phone className="w-8 h-8 text-yellow-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-yellow-900 mb-2">
                ⚡ Action Required
              </h3>
              <p className="text-base text-yellow-800 font-medium">
                Call an {buyerData.name} expert to complete your quote and
                activate your reward
              </p>
            </div>
          </div>
        </div>
      )}
      {/* {isNovo && (
        <div
          style={{ marginBottom: "5px" }}
          className="bg-green-50 border-l-4 border-green-400 rounded-lg p-6 mt-4 mb-6 shadow-sm"
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Info className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <p className="text-[8px] text-green-800 mb-4">
                By clicking “Accept Offer”, you agree that Novo and its
                authorized partners may contact you regarding your quotes,
                applications, policy information, or related services at the
                phone number you provide. You authorize us to contact you by
                phone, or SMS including using an automated dialing system or
                prerecorded voice. Your consent is not a condition of purchase.
                Message and data rates may apply. You also agree that if you
                initiate a call to Novo, or if a partner transfers your call or
                submits your information to Novo, Novo may contact you using the
                communication methods described above.
              </p>
            </div>
          </div>
        </div>
      )} */}
      <div className="!mt-auto space-y-3">
        <button
          onClick={() => {
            trackButtonClick("view-offer", {
              step_name: "buyer_offer",
            });
            handleOfferResponse(true);
          }}
          disabled={isProcessing}
          className={`w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors`}
        >
          {isProcessing ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              {isSimplyIOA
                ? "Continue"
                : isAADirect || isRein
                ? "Accept & Get Call Info"
                : "Accept Offer"}
            </>
          )}
        </button>
        {isRein && (
          <p className="text-xs text-center text-gray-500">
            By clicking on the above button "ACCEPT AND GET CALL INFO", you
            agree to be contacted by Rein by phone or e-mail. Message and data
            rates may apply. You can opt out at any time.
          </p>
        )}
      </div>
      {/* Buyer Details */}
      {/* <Card className="border border-green-100">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center">
            {buyerData.logo && (
              <img
                src={buyerData.logo}
                alt={buyerData.name}
                className="h-12 w-auto mb-4 object-contain"
              />
            )}

            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {buyerData.name} will buy your profile for
              </h2>
              <div className="flex items-center justify-center mt-3">
                <span className="text-3xl font-bold text-green-600">
                  ${buyerData.offer.amount.toFixed(2)}
                </span>
              </div>
            </div>

            <p className="text-gray-600 text-center mb-6">
              {buyerData.shortDescription ||
                buyerData.description ||
                `${buyerData.name} is interested in your insurance profile to provide personalized offers and services.`}
            </p>

            <div className="w-full bg-blue-50 p-4 rounded-lg mb-6">
              <div className="flex items-center gap-2 mb-2">
                <ThumbsUp className="w-5 h-5 text-blue-500" />
                <span className="font-medium text-blue-800">
                  Why Choose {buyerData.name}
                </span>
              </div>
              <ul className="text-sm text-blue-700 space-y-1 pl-7 list-disc">
                <li>Trusted by thousands of customers nationwide</li>
                <li>Partners with top-rated insurance providers</li>
                <li>Fast, personalized quotes in minutes</li>
                <li>Secure and privacy-focused platform</li>
              </ul>
            </div>

            <div className="w-full space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Secure Data Sharing
                  </p>
                  <p className="text-xs text-gray-600">
                    Your data is shared securely with approved partners only
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <DollarSign className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Instant Payment
                  </p>
                  <p className="text-xs text-gray-600">
                    Receive payment immediately in your Ownli wallet
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => handleOfferResponse(true)}
              disabled={isProcessing}
              className="w-full flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-70"
            >
              {isProcessing ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  {isSimplyIOA ? "Confirm Offer" : "Accept Offer"}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <button
              onClick={onReject}
              className="text-sm text-gray-500 hover:text-gray-700 mt-4"
            >
              No thanks, I'll pass
            </button>
          </div>
        </CardContent>
      </Card> */}
      {/* Disclaimer */}
      {/* <p className="text-xs text-center text-gray-500">
        By accepting this offer, you agree to share your insurance profile
        information with {buyerData.name} and their insurance partners in
        accordance with our privacy policy
      </p> */}
    </div>
  );
};

export default BuyerOfferScreen;
